import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { CapacitorUpdater } from '@capgo/capacitor-updater';

import GlobalErrorBoundary from './components/GlobalErrorBoundary/GlobalErrorBoundary';
import OtaBootGate from './components/OtaBootGate';
import { SupportProvider } from './components/Support/SupportContext';
import { NativeVersionGate } from './components/UpdateRequiredScreen';
import ConnectionManagerProvider from './contexts/ConnectionManagerContext';
import { GlobalProvider } from './contexts/GlobalContext';
import { SessionAnimationPreferencesProvider } from './contexts/SessionAnimationPreferencesProvider';
import { SplashScreenProvider } from './contexts/SplashScreenContext';
import { TableSettingsProvider } from './contexts/TableSettingsContext';
import { SessionFeatureFlagProvider } from './featureFlags/SessionFeatureFlagProvider';
import { initStatusBar } from './services/initStatusBar';
import { startVersionGate } from './services/nativeVersionGate';
import { startOtaBoot } from './services/otaBoot';
import { initPendo } from './services/PendoService';
import { adoptWebLoginHandoff } from './utils/auth';
import { warmLocationProvider } from './utils/warmLocationProvider';
import { installGeolocationProbe } from './utils/webviewGeolocationProbe';
import App from './App';
import reportWebVitals from './reportWebVitals';
import store from './store';

import './index.css';

// Must run before the first render — adopts the auth app's /loginapp session
// handoff (see adoptWebLoginHandoff's docblock) so isGuest/getToken already
// see the member session by the time anything mounts.
adoptWebLoginHandoff();

initPendo();
// Keep the native status bar from overlapping top-pinned UI on mobile.
initStatusBar();

// Start the forced-native-update check HERE, before the first render, so its
// one static GET overlaps React boot instead of queueing behind it. Started at
// module scope rather than from a lifecycle listener because it must run on
// COLD START ONLY — a floor raised while someone is at a table must not
// interrupt them. NativeVersionGate below awaits this same promise
// (startVersionGate is idempotent). No-ops on web.
startVersionGate();

// Kick the cold-launch OTA check off HERE too, before the first render, so the
// round trip to /ota/check overlaps React boot instead of queueing behind it —
// that overlap is most of the "no perceptible delay when there is no update"
// budget. OtaBootGate below awaits this same promise (startOtaBoot is
// idempotent) rather than starting a second copy. No-ops on web.
//
// Deliberately started alongside the version gate rather than after it: both
// are cold-start-only, and serialising them would spend the launch budget
// twice over for every player to save wasted work for the few who are about to
// be blocked anyway. See the note on the nesting below.
startOtaBoot();

// Wake the location provider now so the login geofence check is not the thing
// that pays for a cold fix. Measured 10-30s cold versus milliseconds warm on a
// real device. No-op unless location permission is already granted, so this
// never raises a prompt at launch. See warmLocationProvider.
//
// Ordered AFTER the two gates above deliberately. All three are cold-start-only
// and none blocks the others (this one returns immediately and does its work in
// the background), but the gates each own a network round trip that the launch
// budget depends on overlapping with React boot, so they get to start first.
warmLocationProvider();

// Debug-build only. Exposes window.__geoProbe() so a real device can answer
// whether navigator.geolocation works inside the Capacitor WebView. No-op on
// production and live.
installGeolocationProbe();

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

root.render(
  <Provider store={store}>
    <SessionFeatureFlagProvider>
    <ConnectionManagerProvider>
      <GlobalProvider>
        <TableSettingsProvider>
          <SessionAnimationPreferencesProvider>
          <BrowserRouter>
            <SupportProvider>
              {/* App-root boundary: a render crash in the shell / lobby / grid
                  now shows a Reload recovery instead of a blank white screen. */}
              <GlobalErrorBoundary>
                {/* Terminal block when the installed native shell is older
                    than the published minimum. Sits above the splash provider
                    because it is not a loading state that clears — there is no
                    way past it but the store. */}
                <NativeVersionGate>
                  <SplashScreenProvider>
                    {/* Holds the app on the splash while a new bundle
                        downloads, and renders the progress into it. Nothing
                        below mounts until it resolves, which is what keeps the
                        download off live gameplay traffic — the socket only
                        connects once Lobby/Game mounts. A launch with no
                        network at all shows the offline screen from here
                        instead of releasing into a lobby that cannot connect.

                        INSIDE the splash provider because it renders through
                        it, and inside NativeVersionGate because a shell below
                        the floor has nowhere to go: there is no point holding
                        such a player on a download progress bar. */}
                    <OtaBootGate>
                      <App />
                    </OtaBootGate>
                  </SplashScreenProvider>
                </NativeVersionGate>
              </GlobalErrorBoundary>
            </SupportProvider>
          </BrowserRouter>
          </SessionAnimationPreferencesProvider>
        </TableSettingsProvider>
      </GlobalProvider>
    </ConnectionManagerProvider>
    </SessionFeatureFlagProvider>
  </Provider>
);

// Signal to Capgo that the bundle booted successfully — prevents auto-rollback.
CapacitorUpdater.notifyAppReady().catch(() => {});

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
